import {
  AlertCircleIcon,
  ArrowUpRight01Icon,
} from "~/components/icons/central-icons";
import { CentralIcon } from "~/components/icons/central-icons";
import { Link } from "@tanstack/react-router";
import { cva, type VariantProps } from "class-variance-authority";
import { type ReactElement, type ReactNode, useState } from "react";

import { Button } from "~/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { formatSignedPercent } from "~/lib/display-format";
import {
  buildUnifiedSymbolHref,
  type SymbolRouteAssetClass,
} from "~/features/symbol/symbol-route-params";
import {
  type ChainIconSlug,
  getChainIconSlug,
  getChainIconUrl,
} from "~/components/chain-icon";
import { SubscriptCurrency } from "~/components/subscript-currency";
import { m } from "~/lib/i18n";
import { cn } from "~/lib/utils";
import { useMarketValueFormatter } from "~/lib/value-format";

const MINI_SPARKLINE_WIDTH = 88;
const MINI_SPARKLINE_HEIGHT = 28;
const MINI_SPARKLINE_PADDING = 2;
const TOKEN_INFO_OPEN_DELAY_MS = 40;
const TOKEN_INFO_CLOSE_DELAY_MS = 180;

// Shared by the surface variants and the warning branches below: the warning
// treatments swap border/shadow styling but must keep the circular structure.
const TOKEN_GLYPH_STRUCTURE_CLASS_NAME =
  "relative flex size-full overflow-hidden rounded-full border transition-[border-color,box-shadow,filter,opacity] duration-200";

const tokenGlyphSurfaceVariants = cva(TOKEN_GLYPH_STRUCTURE_CLASS_NAME, {
  defaultVariants: {
    shadow: "subtle",
    surface: "framed",
  },
  variants: {
    shadow: {
      none: "shadow-none group-hover/token-root:shadow-none",
      subtle:
        "shadow-[var(--glider-shadow-subtle)] group-hover/token-root:shadow-[var(--glider-shadow-small)]",
    },
    surface: {
      embedded:
        "border-transparent bg-transparent group-hover/token-root:border-transparent",
      framed:
        "border-border/75 bg-background group-hover/token-root:border-foreground/12",
    },
  },
});

export type TokenIconSurface = NonNullable<
  VariantProps<typeof tokenGlyphSurfaceVariants>["surface"]
>;

type ChainTone =
  | "base"
  | "ethereum"
  | "solana"
  | "arbitrum"
  | "bsc"
  | "plume"
  | "robinhood"
  | "hyperevm"
  | "default";

interface ChainPresentation {
  badgeLabel: string;
  iconSlug: ChainIconSlug | null;
  label: string;
  tone: ChainTone;
}

export interface TokenIconMarketPreview {
  assetClass?: SymbolRouteAssetClass;
  breakdownRows?: ReadonlyArray<{
    caption?: string;
    label: string;
    value: string;
  }>;
  change1hPercent?: number;
  change24hPercent?: number;
  chainLabel?: string;
  marketCapUsd?: number;
  name?: string;
  priceUsd?: number;
  sparklineData?: ReadonlyArray<{
    timestamp: number;
    value: number;
  }>;
  subtitle?: string;
  symbol?: string;
}

export interface TokenIconProps {
  alt?: string;
  chainId?: number | string;
  chainLabel?: string;
  className?: string;
  customWarning?: {
    description: ReactNode;
    title: string;
  };
  dimmed?: boolean;
  disableShadow?: boolean;
  disablePreviewTriggerFocus?: boolean;
  disableLink?: boolean;
  grayscale?: boolean;
  hideChainBadge?: boolean;
  highlightWarningBorder?: boolean;
  imageLoading?: "eager" | "lazy";
  isImageLoading?: boolean;
  isBlocked?: boolean;
  isRisky?: boolean;
  preview?: TokenIconMarketPreview;
  size?: number;
  src?: string;
  surface?: TokenIconSurface;
  tokenAddress?: string;
}

function normalizeChainId(chainId: number | string | undefined): string | null {
  if (typeof chainId === "number" && Number.isFinite(chainId)) {
    return String(chainId);
  }

  if (typeof chainId !== "string") {
    return null;
  }

  const trimmed = chainId.trim();
  if (trimmed.length === 0) {
    return null;
  }

  const normalized = trimmed.toLowerCase();
  if (normalized.startsWith("eip155:")) {
    return normalized.slice("eip155:".length);
  }

  if (normalized === "solana:mainnet") {
    return "solana";
  }

  return normalized;
}

function normalizeChainLabel(label: string | undefined): string | null {
  if (typeof label !== "string") {
    return null;
  }

  const trimmed = label.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function resolveChainLabel(
  chainId: number | string | undefined,
  fallbackLabel?: string,
): string | null {
  const normalizedId = normalizeChainId(chainId);
  const normalizedLabel = normalizeChainLabel(fallbackLabel);

  if (
    normalizedLabel &&
    /[a-z]/i.test(normalizedLabel) &&
    !/^\d+$/.test(normalizedLabel)
  ) {
    const lowerLabel = normalizedLabel.toLowerCase();
    if (lowerLabel.includes("solana")) {
      return "Solana";
    }
    if (lowerLabel.includes("ethereum")) {
      return "Ethereum";
    }
    if (lowerLabel.includes("base")) {
      return "Base";
    }
    if (lowerLabel.includes("arbitrum")) {
      return "Arbitrum";
    }
    if (
      lowerLabel.includes("bnb") ||
      lowerLabel.includes("bsc") ||
      lowerLabel.includes("binance smart chain")
    ) {
      return "BNB Chain";
    }
    if (lowerLabel.includes("plume")) {
      return "Plume";
    }
    if (lowerLabel.includes("robinhood")) {
      return "Robinhood Chain";
    }
    if (lowerLabel.includes("hyperevm")) {
      return "HyperEVM";
    }
    if (lowerLabel.includes("monad")) {
      return "Monad";
    }

    return normalizedLabel;
  }

  switch (normalizedId) {
    case "1":
      return "Ethereum";
    case "8453":
      return "Base";
    case "42161":
      return "Arbitrum";
    case "56":
      return "BNB Chain";
    case "10":
      return "Optimism";
    case "137":
      return "Polygon";
    case "130":
      return "Unichain";
    case "59144":
      return "Linea";
    case "81457":
      return "Blast";
    case "98866":
      return "Plume";
    case "4663":
      return "Robinhood Chain";
    case "999":
      return "HyperEVM";
    case "143":
      return "Monad";
    case "solana":
    case "1399811149":
      return "Solana";
    default:
      if (normalizedLabel) {
        return normalizedLabel;
      }

      if (normalizedId) {
        return `Chain ${normalizedId}`;
      }

      return null;
  }
}

function getChainPresentation(
  chainId: number | string | undefined,
  fallbackLabel?: string,
): ChainPresentation | null {
  const normalizedId = normalizeChainId(chainId);
  const label = resolveChainLabel(chainId, fallbackLabel);

  if (!label) {
    return null;
  }

  const iconSlug = getChainIconSlug(chainId, label);

  switch (label) {
    case "Base":
      return { badgeLabel: "B", iconSlug, label, tone: "base" };
    case "Ethereum":
      return { badgeLabel: "E", iconSlug, label, tone: "ethereum" };
    case "Solana":
      return { badgeLabel: "S", iconSlug, label, tone: "solana" };
    case "Arbitrum":
      return { badgeLabel: "A", iconSlug, label, tone: "arbitrum" };
    case "BNB Chain":
      return { badgeLabel: "B", iconSlug, label, tone: "bsc" };
    case "Plume":
      return { badgeLabel: "P", iconSlug, label, tone: "plume" };
    case "Robinhood Chain":
      return { badgeLabel: "R", iconSlug, label, tone: "robinhood" };
    case "HyperEVM":
      return { badgeLabel: "H", iconSlug, label, tone: "hyperevm" };
    default:
      return {
        badgeLabel:
          label === `Chain ${normalizedId}` && normalizedId
            ? normalizedId.slice(0, 1).toUpperCase()
            : label.slice(0, 1).toUpperCase(),
        iconSlug,
        label,
        tone: "default",
      };
  }
}

export function formatTokenChainLabel(
  chainId: number | string | undefined,
  fallbackLabel?: string,
): string {
  return resolveChainLabel(chainId, fallbackLabel) ?? "—";
}

function roundCoordinate(value: number): string {
  return value.toFixed(2);
}

function buildSparklinePath(
  data: ReadonlyArray<{ timestamp: number; value: number }>,
): string {
  if (data.length === 0) {
    return "";
  }

  if (data.length === 1) {
    const centerX = MINI_SPARKLINE_WIDTH / 2;
    const centerY = MINI_SPARKLINE_HEIGHT / 2;
    return `M ${roundCoordinate(centerX)} ${roundCoordinate(centerY)} L ${roundCoordinate(centerX)} ${roundCoordinate(centerY)}`;
  }

  const values = data.map((point) => point.value);
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  const valueRange = maxValue - minValue;
  const width = MINI_SPARKLINE_WIDTH - MINI_SPARKLINE_PADDING * 2;
  const height = MINI_SPARKLINE_HEIGHT - MINI_SPARKLINE_PADDING * 2;

  return data
    .map((point, index) => {
      const x =
        MINI_SPARKLINE_PADDING + (width * index) / Math.max(1, data.length - 1);
      const normalizedY =
        valueRange === 0 ? 0.5 : (point.value - minValue) / valueRange;
      const y = MINI_SPARKLINE_PADDING + height * (1 - normalizedY);
      return `${index === 0 ? "M" : "L"} ${roundCoordinate(x)} ${roundCoordinate(y)}`;
    })
    .join(" ");
}

function getMetricTone(
  value: number | undefined,
): "negative" | "neutral" | "positive" {
  if (typeof value !== "number" || !Number.isFinite(value) || value === 0) {
    return "neutral";
  }

  return value > 0 ? "positive" : "negative";
}

function getMetricToneClassName(value: number | undefined): string {
  const tone = getMetricTone(value);
  if (tone === "positive") {
    return "text-status-positive-foreground";
  }
  if (tone === "negative") {
    return "text-status-negative-foreground";
  }
  return "text-foreground";
}

function getSparklineStroke(value: number | undefined): string {
  const tone = getMetricTone(value);
  if (tone === "positive") {
    return "var(--portfolio-trend-positive)";
  }
  if (tone === "negative") {
    return "var(--portfolio-trend-negative)";
  }
  return "var(--muted-foreground)";
}

function getChainBadgeClassName(tone: ChainTone): string {
  switch (tone) {
    case "base":
      return "border-sky-300/80 bg-sky-500 text-white dark:border-sky-700/60 dark:bg-sky-500";
    case "ethereum":
      return "border-slate-300/80 bg-slate-800 text-white dark:border-slate-600/60 dark:bg-slate-200 dark:text-slate-950";
    case "solana":
      return "border-violet-300/70 bg-linear-to-r from-emerald-400 via-sky-500 to-violet-500 text-white dark:border-violet-600/60";
    case "arbitrum":
      return "border-cyan-300/80 bg-cyan-500 text-white dark:border-cyan-700/60 dark:bg-cyan-500";
    case "bsc":
      return "border-yellow-300/90 bg-yellow-400 text-black dark:border-yellow-600/60 dark:bg-yellow-400";
    case "plume":
      return "border-fuchsia-300/80 bg-fuchsia-500 text-white dark:border-fuchsia-700/60 dark:bg-fuchsia-500";
    case "robinhood":
      return "border-emerald-300/80 bg-emerald-500 text-white dark:border-emerald-700/60 dark:bg-emerald-500";
    case "hyperevm":
      return "border-teal-200/80 bg-teal-300 text-black dark:border-teal-500/60 dark:bg-teal-300";
    default:
      return "border-border/80 bg-background/95 text-foreground";
  }
}

function getWarningVariant(
  input: Pick<
    TokenIconProps,
    "customWarning" | "highlightWarningBorder" | "isBlocked" | "isRisky"
  >,
): "blocked" | "warning" | null {
  if (input.isBlocked) {
    return "blocked";
  }

  if (input.isRisky || input.highlightWarningBorder || input.customWarning) {
    return "warning";
  }

  return null;
}

function WarningIndicator({
  size,
  variant,
}: {
  size: number;
  variant: "blocked" | "warning";
}) {
  return (
    <span
      className={cn(
        "absolute -right-1 -top-1 z-20 flex items-center justify-center rounded-full border bg-background shadow-[var(--glider-shadow-small)]",
        variant === "blocked"
          ? "border-red-300/90 text-status-negative-foreground dark:border-red-800/70"
          : "border-amber-300/90 text-status-warning-foreground dark:border-amber-800/70",
      )}
      style={{ height: size, width: size }}
    >
      <CentralIcon
        className="size-[68%]"
        icon={AlertCircleIcon}
        strokeWidth={2.2}
      />
    </span>
  );
}

function TokenGlyph({
  alt,
  chainPresentation,
  dimmed = false,
  disableShadow = false,
  grayscale = false,
  hideChainBadge = false,
  imageLoading = "lazy",
  imageUrl,
  isImageLoading = false,
  size,
  symbol,
  surface = "framed",
  warningVariant,
}: {
  alt: string;
  chainPresentation: ChainPresentation | null;
  dimmed?: boolean;
  disableShadow?: boolean;
  grayscale?: boolean;
  hideChainBadge?: boolean;
  imageLoading?: "eager" | "lazy";
  imageUrl?: string;
  isImageLoading?: boolean;
  size: number;
  symbol: string;
  surface?: TokenIconSurface;
  warningVariant: "blocked" | "warning" | null;
}) {
  const [failedImageUrl, setFailedImageUrl] = useState<string | null>(null);
  const badgeSize = Math.max(13, Math.round(size * 0.36));
  const indicatorSize = Math.max(16, Math.round(size * 0.44));
  const shouldRenderImage = Boolean(imageUrl && imageUrl !== failedImageUrl);
  const fallbackText =
    symbol
      .trim()
      .replace(/[^a-z0-9]/giu, "")
      .slice(0, 4)
      .toUpperCase() || "?";
  const fallbackFontSize =
    fallbackText.length >= 4
      ? Math.max(8, Math.round(size * 0.21))
      : fallbackText.length >= 3
        ? Math.max(9, Math.round(size * 0.24))
        : Math.max(10, Math.round(size * 0.28));

  return (
    <span
      className={cn("group/token relative inline-flex shrink-0 align-middle")}
      style={{ height: size, width: size }}
    >
      {warningVariant ? (
        <WarningIndicator size={indicatorSize} variant={warningVariant} />
      ) : null}

      <span
        className={cn(
          warningVariant === "blocked"
            ? cn(
                TOKEN_GLYPH_STRUCTURE_CLASS_NAME,
                "bg-background border-red-400/90 shadow-[0_0_0_2px_rgba(248,113,113,0.12),var(--glider-shadow-small)] dark:border-red-500/75",
              )
            : warningVariant === "warning"
              ? cn(
                  TOKEN_GLYPH_STRUCTURE_CLASS_NAME,
                  "bg-background border-amber-400/90 shadow-[0_0_0_2px_rgba(251,191,36,0.14),var(--glider-shadow-small)] dark:border-amber-500/75",
                )
              : tokenGlyphSurfaceVariants({
                  shadow:
                    disableShadow || surface === "embedded" ? "none" : "subtle",
                  surface,
                }),
          grayscale && "grayscale",
          dimmed && "opacity-55",
        )}
      >
        {shouldRenderImage && imageUrl ? (
          <img
            alt={alt}
            className="size-full object-cover"
            loading={imageLoading}
            onError={() => setFailedImageUrl(imageUrl)}
            src={imageUrl}
          />
        ) : isImageLoading ? (
          <span
            aria-hidden="true"
            className="size-full animate-pulse bg-muted"
          />
        ) : (
          <span
            className="flex size-full items-center justify-center bg-muted font-semibold uppercase text-muted-foreground"
            style={{ fontSize: fallbackFontSize, letterSpacing: 0 }}
          >
            {fallbackText}
          </span>
        )}
      </span>

      {chainPresentation && !hideChainBadge ? (
        chainPresentation.iconSlug ? (
          <span
            className={cn(
              "absolute -bottom-1 -right-1 z-10 inline-flex overflow-hidden rounded-full border-[1.5px] border-background bg-background",
              disableShadow
                ? "shadow-none"
                : "shadow-[var(--glider-shadow-small)]",
            )}
            style={{ height: badgeSize, width: badgeSize }}
            title={chainPresentation.label}
          >
            <img
              alt={chainPresentation.label}
              className="size-full"
              loading="lazy"
              src={getChainIconUrl(chainPresentation.iconSlug, "background")}
            />
          </span>
        ) : (
          <span
            className={cn(
              "absolute -bottom-1 -right-1 z-10 inline-flex items-center justify-center rounded-[0.55rem] border",
              disableShadow
                ? "shadow-none"
                : "shadow-[var(--glider-shadow-small)]",
              getChainBadgeClassName(chainPresentation.tone),
            )}
            style={{
              fontSize: Math.max(8, Math.round(size * 0.18)),
              height: badgeSize,
              minWidth: badgeSize,
              paddingInline: Math.max(4, Math.round(size * 0.08)),
            }}
            title={chainPresentation.label}
          >
            <span className="font-semibold leading-none tracking-[0.03em]">
              {chainPresentation.badgeLabel}
            </span>
          </span>
        )
      ) : null}
    </span>
  );
}

function PreviewMetric({
  label,
  value,
  valueClassName,
}: {
  label: string;
  value: ReactNode;
  valueClassName?: string;
}) {
  return (
    <div className="page-surface-tile min-w-0 rounded-lg px-3 py-2">
      <p className="text-kicker font-medium uppercase text-muted-foreground">
        {label}
      </p>
      <div
        className={cn(
          "mt-1 truncate text-caption font-medium tabular-nums text-foreground",
          valueClassName,
        )}
      >
        {value}
      </div>
    </div>
  );
}

function Sparkline({
  change24hPercent,
  data,
}: {
  change24hPercent?: number;
  data: ReadonlyArray<{ timestamp: number; value: number }>;
}) {
  return (
    <div className="h-7 w-20">
      <svg
        aria-hidden="true"
        className="block h-full w-full overflow-visible"
        preserveAspectRatio="none"
        viewBox={`0 0 ${MINI_SPARKLINE_WIDTH} ${MINI_SPARKLINE_HEIGHT}`}
      >
        <path
          d={buildSparklinePath(data)}
          fill="none"
          stroke={getSparklineStroke(change24hPercent)}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          vectorEffect="non-scaling-stroke"
        />
      </svg>
    </div>
  );
}

function TokenMarketPreviewPopoverContent(props: {
  customWarning?: TokenIconProps["customWarning"];
  glyph: ReactNode;
  preview: TokenIconMarketPreview | undefined;
  previewPageUrl: string | null;
  resolvedName: string;
  resolvedSymbol: string;
  warningVariant: "blocked" | "warning" | null;
}) {
  const marketValueFormatter = useMarketValueFormatter();
  const { customWarning, preview, previewPageUrl, warningVariant } = props;

  return (
    <PopoverContent
      align="start"
      className={cn(
        "z-[70] w-80 max-w-[calc(100vw-1rem)] rounded-[1.45rem] p-0",
        warningVariant === "blocked" &&
          "border-red-300/80 dark:border-red-900/60",
        warningVariant === "warning" &&
          "border-amber-300/80 dark:border-amber-900/60",
      )}
      data-dashboard-chart-hover-blocker="true"
      finalFocus={false}
      initialFocus={false}
      side="top"
      sideOffset={6}
    >
      <div className="overflow-hidden rounded-[inherit]">
        {warningVariant ? (
          <div
            className={cn(
              "border-b px-4 py-3",
              warningVariant === "blocked"
                ? "border-red-300/50 bg-red-500/10 dark:border-red-900/40"
                : "border-amber-300/50 bg-amber-500/10 dark:border-amber-900/40",
            )}
          >
            <p
              className={cn(
                "flex items-center gap-2 text-meta font-semibold",
                warningVariant === "blocked"
                  ? "text-status-negative-foreground"
                  : "text-status-warning-foreground",
              )}
            >
              <CentralIcon className="size-4" icon={AlertCircleIcon} />
              {warningVariant === "blocked"
                ? m.shared_token_warning_blocked_title()
                : (customWarning?.title ??
                  m.shared_token_warning_elevated_risk_title())}
            </p>
            <p className="mt-1 text-caption leading-5 text-muted-foreground">
              {warningVariant === "blocked"
                ? m.shared_token_warning_blocked_portfolio_description()
                : (customWarning?.description ??
                  m.shared_token_warning_market_risk_description())}
            </p>
          </div>
        ) : null}

        <div className="space-y-4 p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              {props.glyph}

              <div className="min-w-0">
                <p className="truncate text-meta font-semibold text-foreground">
                  {props.resolvedName}
                </p>
                <span className="mt-1 block text-caption text-muted-foreground">
                  {props.resolvedSymbol}
                </span>
                {preview?.subtitle ? (
                  <span className="mt-0.5 block truncate text-caption text-muted-foreground">
                    {preview.subtitle}
                  </span>
                ) : null}
              </div>
            </div>

            {preview?.sparklineData && preview.sparklineData.length > 0 ? (
              <Sparkline
                change24hPercent={preview.change24hPercent}
                data={preview.sparklineData}
              />
            ) : null}
          </div>

          <div className="grid grid-cols-2 gap-2.5">
            <PreviewMetric
              label={m.components_token_icon_current_price_label()}
              value={<SubscriptCurrency value={preview?.priceUsd} />}
            />
            <PreviewMetric
              label={m.components_token_icon_market_cap_label()}
              value={marketValueFormatter.compactCurrency(
                preview?.marketCapUsd,
              )}
            />
            <PreviewMetric
              label={m.components_token_icon_one_hour_label({ period: "1h" })}
              value={formatSignedPercent(preview?.change1hPercent)}
              valueClassName={getMetricToneClassName(preview?.change1hPercent)}
            />
            <PreviewMetric
              label={m.components_token_icon_twenty_four_hour_label({
                period: "24h",
              })}
              value={formatSignedPercent(preview?.change24hPercent)}
              valueClassName={getMetricToneClassName(preview?.change24hPercent)}
            />
          </div>

          {preview?.breakdownRows?.length ? (
            <div className="page-surface-tile space-y-2 rounded-lg p-3">
              <p className="text-kicker font-medium uppercase text-muted-foreground">
                {m.components_token_icon_breakdown_heading()}
              </p>
              <div className="space-y-2">
                {preview.breakdownRows.slice(0, 5).map((row) => (
                  <div
                    className="grid grid-cols-[minmax(0,1fr)_auto] gap-3"
                    key={`${row.label}:${row.value}:${row.caption ?? ""}`}
                  >
                    <div className="min-w-0">
                      <p className="truncate text-meta font-medium text-foreground">
                        {row.label}
                      </p>
                      {row.caption ? (
                        <p className="truncate text-meta text-muted-foreground">
                          {row.caption}
                        </p>
                      ) : null}
                    </div>
                    <p className="shrink-0 text-meta font-medium tabular-nums text-foreground">
                      {row.value}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {previewPageUrl ? (
            <Button asChild className="w-full" size="sm" variant="outline">
              <Link to={previewPageUrl}>
                {m.components_token_icon_view_asset_label()}
                <CentralIcon className="size-4" icon={ArrowUpRight01Icon} />
              </Link>
            </Button>
          ) : null}
        </div>
      </div>
    </PopoverContent>
  );
}

/**
 * Hover/press price peek for an already-interactive trigger (e.g. a ticker
 * badge link). Renders the same preview card as TokenIcon's popover without
 * requiring the icon glyph to be the trigger.
 */
export function TokenMarketPreviewPopover(props: {
  iconUrl?: string | null;
  preview: TokenIconMarketPreview;
  trigger: ReactElement;
}) {
  const resolvedSymbol = props.preview.symbol?.trim() || "Token";
  const resolvedName = props.preview.name?.trim() || resolvedSymbol;
  const previewPageUrl = buildUnifiedSymbolHref(props.preview.symbol);

  return (
    <Popover>
      <PopoverTrigger
        closeDelay={TOKEN_INFO_CLOSE_DELAY_MS}
        delay={TOKEN_INFO_OPEN_DELAY_MS}
        nativeButton={false}
        openOnHover
        render={props.trigger}
      />
      <TokenMarketPreviewPopoverContent
        glyph={
          <TokenGlyph
            alt={m.components_token_icon_symbol_icon_alt({
              symbol: resolvedSymbol,
            })}
            chainPresentation={getChainPresentation(
              undefined,
              props.preview.chainLabel,
            )}
            hideChainBadge
            imageUrl={props.iconUrl ?? undefined}
            size={40}
            symbol={resolvedSymbol}
            warningVariant={null}
          />
        }
        preview={props.preview}
        previewPageUrl={previewPageUrl}
        resolvedName={resolvedName}
        resolvedSymbol={resolvedSymbol}
        warningVariant={null}
      />
    </Popover>
  );
}

export function TokenIcon({
  alt: providedAlt,
  chainId,
  chainLabel,
  className,
  customWarning,
  dimmed = false,
  disablePreviewTriggerFocus = false,
  disableShadow = false,
  disableLink = false,
  grayscale = false,
  hideChainBadge = false,
  highlightWarningBorder = false,
  imageLoading = "lazy",
  isImageLoading = false,
  isBlocked = false,
  isRisky = false,
  preview,
  size = 40,
  src,
  surface = "framed",
}: TokenIconProps) {
  const alt = providedAlt ?? "Token";
  const warningVariant = getWarningVariant({
    customWarning,
    highlightWarningBorder,
    isBlocked,
    isRisky,
  });
  const resolvedSymbol =
    preview?.symbol?.trim() || alt.trim().replace(/\s+icon$/i, "") || "Token";
  const resolvedName = preview?.name?.trim() || resolvedSymbol;
  const resolvedChainPresentation = getChainPresentation(
    chainId,
    chainLabel ?? preview?.chainLabel,
  );
  const symbolPageUrl = buildUnifiedSymbolHref(
    preview?.symbol ?? providedAlt?.trim().replace(/\s+icon$/i, "") ?? null,
  );
  const previewPageUrl = preview ? symbolPageUrl : null;
  const shouldLink = Boolean(symbolPageUrl && !disableLink);
  const shouldShowPreview = Boolean(preview);

  const glyph = (
    <TokenGlyph
      alt={alt}
      chainPresentation={resolvedChainPresentation}
      dimmed={dimmed}
      disableShadow={disableShadow}
      grayscale={grayscale}
      hideChainBadge={hideChainBadge}
      imageLoading={imageLoading}
      imageUrl={src}
      isImageLoading={isImageLoading}
      size={size}
      symbol={resolvedSymbol}
      surface={surface}
      warningVariant={warningVariant}
    />
  );

  const content = (
    <span
      className={cn("group/token-root inline-flex rounded-full", className)}
    >
      {glyph}
    </span>
  );

  function createTriggerElement(ariaLabel: string): ReactElement {
    if (shouldLink && symbolPageUrl) {
      return (
        <Link
          aria-label={ariaLabel}
          className="inline-flex rounded-full glider-focus-ring"
          preload="intent"
          to={symbolPageUrl}
        >
          {content}
        </Link>
      );
    }

    if (!disablePreviewTriggerFocus) {
      return (
        <button
          aria-label={ariaLabel}
          className="inline-flex rounded-full glider-focus-ring"
          type="button"
        >
          {content}
        </button>
      );
    }

    return (
      <span
        aria-label={ariaLabel}
        className="inline-flex rounded-full glider-focus-ring"
      >
        {content}
      </span>
    );
  }

  if (shouldShowPreview) {
    return (
      <Popover>
        <PopoverTrigger
          closeDelay={TOKEN_INFO_CLOSE_DELAY_MS}
          delay={TOKEN_INFO_OPEN_DELAY_MS}
          nativeButton={!shouldLink && !disablePreviewTriggerFocus}
          openOnHover
          render={createTriggerElement(`${resolvedSymbol} token details`)}
        />
        <TokenMarketPreviewPopoverContent
          customWarning={customWarning}
          glyph={
            <TokenGlyph
              alt={alt}
              chainPresentation={resolvedChainPresentation}
              dimmed={dimmed}
              disableShadow={disableShadow}
              grayscale={grayscale}
              hideChainBadge
              imageUrl={src}
              isImageLoading={isImageLoading}
              size={40}
              symbol={resolvedSymbol}
              warningVariant={warningVariant}
            />
          }
          preview={preview}
          previewPageUrl={previewPageUrl}
          resolvedName={resolvedName}
          resolvedSymbol={resolvedSymbol}
          warningVariant={warningVariant}
        />
      </Popover>
    );
  }

  if (warningVariant) {
    const tooltipTrigger = createTriggerElement(
      `${resolvedSymbol} warning details`,
    );

    return (
      <Tooltip>
        <TooltipTrigger delay={120} render={tooltipTrigger} />
        <TooltipContent className="max-w-72 rounded-2xl px-3 py-2.5" side="top">
          <div className="space-y-1.5">
            <p
              className={cn(
                "flex items-center gap-2 text-meta font-semibold",
                warningVariant === "blocked"
                  ? "text-status-negative-foreground"
                  : "text-status-warning-foreground",
              )}
            >
              <CentralIcon className="size-4" icon={AlertCircleIcon} />
              {warningVariant === "blocked"
                ? m.shared_token_warning_blocked_title()
                : (customWarning?.title ??
                  m.shared_token_warning_elevated_risk_title())}
            </p>
            <p className="text-caption leading-5 text-muted-foreground">
              {warningVariant === "blocked"
                ? m.shared_token_warning_blocked_reliance_description()
                : (customWarning?.description ??
                  m.shared_token_warning_backend_metadata_description())}
            </p>
          </div>
        </TooltipContent>
      </Tooltip>
    );
  }

  if (shouldLink && symbolPageUrl) {
    return (
      <Link
        aria-label={m.components_token_icon_view_asset_details_label({
          symbol: resolvedSymbol,
        })}
        className="inline-flex rounded-full glider-focus-ring"
        preload="intent"
        to={symbolPageUrl}
      >
        {content}
      </Link>
    );
  }

  return content;
}
